import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaWhatsapp, FaUser, FaIdCard, FaPhone, FaEnvelope, FaCity, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, isValid, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

import { supabase, supabaseUrl } from '../lib/supabaseClient';
import html2canvas from 'html2canvas'; // Import html2canvas

import ReservationSummary from './ReservationSummary'; // Importar el componente de resumen

const ReservationModal = ({ isOpen, onClose, isAdmin = false, existingReservation = null }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    dni: '',
    phone: '',
    email: '',
    guests: '1',
    children: '0',
    checkin: '',
    checkout: '',
    city: ''
  });
  const [roomTypes, setRoomTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [summaryReservation, setSummaryReservation] = useState(null); // State for the summary data
  const [status, setStatus] = useState('pending');
  const [assignedRoom, setAssignedRoom] = useState([]);
  const [amountPaid, setAmountPaid] = useState(0);
  const [dbRoomTypes, setDbRoomTypes] = useState([]); // New state for fetched room types
  const [range, setRange] = useState();

  useEffect(() => {
    const fetchDbRoomTypes = async () => {
      const { data, error } = await supabase
        .from('room_types')
        .select('id, name, price_per_night')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching room types:', error);
      } else {
        setDbRoomTypes(data.map(rt => ({
          type: rt.name,
          quantity: 0,
          pricePerNight: parseFloat(rt.price_per_night) // Ensure it's a number
        })));
      }
    };
    fetchDbRoomTypes();
  }, []);
  
  // Sincronizar el estado del rango con el formData
  useEffect(() => {
    if (range?.from) {
      setFormData(prev => ({ ...prev, checkin: format(range.from, 'yyyy-MM-dd') }));
    } else {
      setFormData(prev => ({ ...prev, checkin: '' }));
    }
    if (range?.to) {
      setFormData(prev => ({ ...prev, checkout: format(range.to, 'yyyy-MM-dd') }));
    } else {
      setFormData(prev => ({ ...prev, checkout: '' }));
    }
  }, [range]);


  const reservationSummaryRef = useRef(null);

  const formatDateForInput = (date) => {
    if (!date) return '';
    // Las fechas de Supabase pueden venir como 'YYYY-MM-DD' o 'YYYY-MM-DDTHH:mm:ss...'.
    // El input[type=date] requiere 'YYYY-MM-DD', así que tomamos la primera parte.
    return date.split('T')[0];
  };

  // Helper to calculate number of nights
  const calculateNights = (checkin, checkout) => {
    if (!checkin || !checkout) return 0;
    const checkinDate = new Date(checkin);
    const checkoutDate = new Date(checkout);
    const timeDiff = checkoutDate.getTime() - checkinDate.getTime();
    const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return dayDiff > 0 ? dayDiff : 0;
  };

  const totalNights = calculateNights(formData.checkin, formData.checkout);
  const totalCost = roomTypes.reduce((sum, room) => sum + (room.quantity * room.pricePerNight * totalNights), 0);

  // --- DEBUGGING ---
  console.log('--- Recalculando Costo ---');
  console.log('Fechas:', formData.checkin, '->', formData.checkout);
  console.log('Noches Totales:', totalNights);
  console.log('Tipos de Habitación (con precios y cantidad):', roomTypes);
  console.log('Costo Total Calculado:', totalCost);
  // --- FIN DEBUGGING ---



  // Efecto para manejar la apertura y el llenado del formulario
  useEffect(() => {
    if (isOpen && dbRoomTypes.length > 0) { // Ensure dbRoomTypes are loaded
      document.body.style.overflow = 'hidden';

      if (existingReservation) {
        // --- MODO EDICIÓN: Rellenar el formulario ---
        const checkinDate = parseISO(existingReservation.checkin_date);
        const checkoutDate = parseISO(existingReservation.checkout_date);
        const newRange = {
            from: isValid(checkinDate) ? checkinDate : undefined,
            to: isValid(checkoutDate) ? checkoutDate : undefined
        };
        setRange(newRange);

        setFormData({
          name: existingReservation.full_name || '',
          dni: existingReservation.dni || '',
          phone: existingReservation.phone || '',
          email: existingReservation.email || '',
          guests: existingReservation.number_of_guests?.toString() || '1',
          children: existingReservation.number_of_children?.toString() || '0',
          checkin: formatDateForInput(existingReservation.checkin_date),
          checkout: formatDateForInput(existingReservation.checkout_date),
          city: existingReservation.city_of_origin || ''
        });
        
        // Populate roomTypes from dbRoomTypes, applying quantities from existingReservation
        const newRoomTypes = dbRoomTypes.map(dbRt => {
          const existingRt = existingReservation.room_types?.find(r => r.type === dbRt.type);
          return {
            ...dbRt,
            quantity: existingRt ? existingRt.quantity : 0
          };
        });
        setRoomTypes(newRoomTypes);

        setStatus(existingReservation.status || 'pending');
        // Initialize assignedRoom from existingReservation.assigned_room_number (which is now an array)
        setAssignedRoom(existingReservation.assigned_room_number || []);
        setAmountPaid(existingReservation.amount_paid || 0);

      } else {
        // --- MODO CREACIÓN: Resetear el formulario ---
        setRange(undefined);
        setFormData({ name: '', dni: '', phone: '', email: '', guests: '1', children: '0', checkin: '', checkout: '', city: '' });
        // Initialize roomTypes from fetched dbRoomTypes with quantity 0
        setRoomTypes(dbRoomTypes.map(dbRt => ({ ...dbRt, quantity: 0 })));
        setStatus('pending');
        setAssignedRoom([]);
        setAmountPaid(0);
      }

      // Siempre resetear el estado de éxito al abrir
      setSubmissionSuccess(false);
      setSummaryReservation(null);
      setCurrentStep(1); // Reset to first step
      
    } else if (!isOpen) {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, existingReservation, dbRoomTypes]);

  // Download summary function, to be triggered by animation completion
  const downloadSummary = async (summaryData) => {
    if (!submissionSuccess || !reservationSummaryRef.current || !summaryData) return;

    try {
      // Temporarily set fixed width for image capture
      const summaryElement = reservationSummaryRef.current;
      const originalWidth = summaryElement.style.width;
      const originalMaxWidth = summaryElement.style.maxWidth;
      summaryElement.style.width = '800px';
      summaryElement.style.maxWidth = '800px';
      
      // Give a small delay to ensure styles are flushed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(summaryElement, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      // Restore original width after capture
      summaryElement.style.width = originalWidth;
      summaryElement.style.maxWidth = originalMaxWidth;

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `comprobante-reserva-sumaq-mayu-${summaryData.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating image:", error);
      alert("No se pudo generar el comprobante de imagen, pero tu reserva fue enviada. Nos contactaremos contigo.");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const openWhatsAppWithDetails = () => {
    const selectedRooms = roomTypes.filter(r => r.quantity > 0);
    const roomsText = selectedRooms.map(r => `${r.quantity}x ${r.type}`).join(', ');
    const nights = calculateNights(formData.checkin, formData.checkout);
    const cost = selectedRooms.reduce((sum, r) => sum + (r.quantity * r.pricePerNight * nights), 0);

    const message = [
      `Hola! Acabo de registrar una reserva en Sumaq Mayu:`,
      ``,
      `*Nombre:* ${formData.name}`,
      `*DNI:* ${formData.dni}`,
      `*Celular:* ${formData.phone}`,
      `*Email:* ${formData.email}`,
      `*Ciudad:* ${formData.city}`,
      ``,
      `*Check-in:* ${formatDate(formData.checkin)}`,
      `*Check-out:* ${formatDate(formData.checkout)}`,
      `*Noches:* ${nights}`,
      ``,
      `*Habitaciones:* ${roomsText}`,
      `*Huespedes:* ${formData.guests} adultos, ${formData.children} ninos`,
      ``,
      `*Total:* S/ ${cost.toFixed(2)}`,
    ].join('\n');

    window.open(`https://wa.me/51947791970?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleWhatsApp = () => {
    openWhatsAppWithDetails();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleRoomQuantityChange = (index, value) => {
    const newRoomTypes = [...roomTypes];
    newRoomTypes[index].quantity = parseInt(value) || 0;
    setRoomTypes(newRoomTypes);
  };

  const handleAssignedRoomChange = (roomNumber) => {
    setAssignedRoom(prevAssignedRoom => {
      if (prevAssignedRoom.includes(roomNumber)) {
        // Remove room number if already selected
        return prevAssignedRoom.filter(num => num !== roomNumber);
      } else {
        // Add room number if not selected
        return [...prevAssignedRoom, roomNumber].sort((a, b) => a - b);
      }
    });
  };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      const selectedRooms = roomTypes.filter(room => room.quantity > 0);
      
      if (selectedRooms.length === 0 && !existingReservation) { // Solo requerir habitaciones si se está creando
        alert('Por favor selecciona al menos un tipo de habitación.');
        return;
      }
      
      setIsSubmitting(true);
  
      try {
        const totalRooms = selectedRooms.reduce((sum, room) => sum + room.quantity, 0);
        
        const reservationData = {
          full_name: formData.name,
          dni: formData.dni,
          phone: formData.phone,
          email: formData.email,
          number_of_rooms: totalRooms,
          number_of_guests: parseInt(formData.guests),
          number_of_children: parseInt(formData.children),
          room_type: selectedRooms[0]?.type, // Usar optional chaining por si no hay habitaciones
          room_types: selectedRooms,
          checkin_date: formData.checkin,
          checkout_date: formData.checkout,
          city_of_origin: formData.city,
          status: isAdmin ? status : 'pending',
          assigned_room_number: assignedRoom.length > 0 ? assignedRoom : null,
          total_cost: totalCost,
          amount_paid: amountPaid,
          amount_due: totalCost - amountPaid
        };
  
        let newReservation;
        
        if (existingReservation && isAdmin) {
          // --- MODO EDICIÓN (Admin) ---
          const functionUrl = `${supabaseUrl}/functions/v1/update-reservation`;
          const { apikey, Authorization } = supabase.auth.headers;
          
          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', apikey, Authorization },
            body: JSON.stringify({
              reservationId: existingReservation.id,
              reservationData: reservationData
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Error al actualizar la reserva: ${response.status} ${errorText}`);
          }
          
          newReservation = await response.json();
          console.log('Reserva actualizada via Edge Function:', newReservation);

        } else if (existingReservation) {
          // --- MODO EDICIÓN (No Admin - si existiera tal caso) ---
          const { data, error } = await supabase
            .from('reservations')
            .update(reservationData)
            .eq('id', existingReservation.id)
            .select();
          
          if (error) throw error;
          newReservation = data[0];
          console.log('Reserva actualizada:', newReservation);
  
        } else {
          // --- MODO CREACIÓN ---
          const { data, error } = await supabase
            .from('reservations')
            .insert([reservationData])
            .select();
          
          if (error) throw error;
          newReservation = data[0];
          console.log('Reserva creada:', newReservation);
  
          // Invocar Edge Function solo al crear
          if (!isAdmin) {
            try {
              const functionUrl = `${supabaseUrl}/functions/v1/send-reservation-email`;
              const { apikey, Authorization } = supabase.auth.headers;
              const response = await fetch(functionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', apikey, Authorization },
                body: JSON.stringify({
                  fullName: formData.name,
                  email: formData.email,
                  phone: formData.phone,
                  checkInDate: formData.checkin,
                  checkOutDate: formData.checkout,
                  adults: parseInt(formData.guests),
                  children: parseInt(formData.children),
                  roomType: selectedRooms.map(r => `${r.quantity}x ${r.type}`).join(', '),
                  message: 'Solicitud de reserva enviada a través del sitio web.'
                }),
              });
              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Edge Function returned a non-2xx status code: ${response.status} ${errorText}`);
              }
              console.log('Email notification function invoked successfully.');
            } catch (functionError) {
              console.error('Error invoking email notification function:', functionError.message);
            }
          }
        }
  
        // After successful submission, create a complete object for the summary
        setSummaryReservation(newReservation);
        setSubmissionSuccess(true);

        // Auto-open WhatsApp with reservation details (only for new user reservations)
        if (!isAdmin && !existingReservation) {
          openWhatsAppWithDetails();
        }
  
      } catch (error) {
        console.error('Error al guardar la reserva:', error.message);
        alert('Hubo un error al guardar la reserva. Por favor, inténtalo de nuevo más tarde.');
      } finally {
        setIsSubmitting(false);
      }
    };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, isAdmin ? 4 : 3));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const canProceedToNextStep = () => {
    switch(currentStep) {
      case 1:
        return formData.name && formData.dni && formData.phone && formData.email && formData.city;
      case 2:
        return formData.checkin && formData.checkout;
      case 3:
        return roomTypes.some(room => room.quantity > 0);
      default:
        return true;
    }
  };

  const renderStepIndicator = () => {
    const totalSteps = isAdmin ? 4 : 3;
    return (
      <div className="flex justify-center items-center gap-2 mb-6">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
          <div key={step} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-all ${
              step === currentStep 
                ? 'bg-green-600 text-white scale-110' 
                : step < currentStep 
                  ? 'bg-green-400 text-white' 
                  : 'bg-gray-300 text-gray-600'
            }`}>
              {step}
            </div>
            {step < totalSteps && <div className={`w-12 h-1 mx-1 ${step < currentStep ? 'bg-green-400' : 'bg-gray-300'}`} />}
          </div>
        ))}
      </div>
    );
  };

  const renderStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-5"
          >
            <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-4">Paso 1: Datos Personales</h3>
            <div>
              <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-700">Nombre Completo</label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Tu nombre completo" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
            <div>
              <label htmlFor="dni" className="block mb-2 text-sm font-medium text-gray-700">DNI</label>
              <div className="relative">
                <FaIdCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" id="dni" name="dni" value={formData.dni} onChange={handleChange} placeholder="Tu número de DNI" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
            <div>
              <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-700">Celular</label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="987 654 321" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700">Email</label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="tu@email.com" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
            <div>
              <label htmlFor="city" className="block mb-2 text-sm font-medium text-gray-700">Ciudad de Procedencia</label>
              <div className="relative">
                <FaCity className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} placeholder="Tu ciudad" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
          </motion.div>
        );
      
      case 2:
        let footerText = 'Por favor, selecciona el primer día de tu estadía.';
        if (range?.from) {
          if (!range.to) {
            footerText = `Llegada: ${format(range.from, 'd LLL y', { locale: es })}. Ahora selecciona tu salida.`;
          } else if (range.to) {
            const nights = calculateNights(formData.checkin, formData.checkout);
            footerText = `Total: ${nights} ${nights === 1 ? 'noche' : 'noches'} de estadía.`;
          }
        }

        return (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex flex-col items-center"
          >
            <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-2">Paso 2: Fechas de Estadía</h3>
            <p className="text-gray-500 text-center mb-4 text-sm">{footerText}</p>
            <DayPicker
              id="day-picker"
              mode="range"
              locale={es}
              selected={range}
              onSelect={setRange}
              disabled={{ before: new Date() }}
              className="w-full flex justify-center"
              numberOfMonths={1}
            />
            
            {range?.from && range?.to && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 w-full text-center">
                 <p className="font-semibold text-blue-800">Llegada: {format(range.from, 'PPP', { locale: es })}</p>
                 <p className="font-semibold text-blue-800">Salida: {format(range.to, 'PPP', { locale: es })}</p>
              </div>
            )}
          </motion.div>
        );
      
      case 3:
        return (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-5"
          >
            <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-4">Paso 3: Habitaciones y Huéspedes</h3>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Tipos de Habitación</label>
              <div className="space-y-3">
                {roomTypes.map((room, index) => (
                  <div key={room.type} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-800">{room.type}</p>
                        <p className="text-sm text-gray-600">S/. {room.pricePerNight.toFixed(2)} / noche</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRoomQuantityChange(index, Math.max(0, room.quantity - 1).toString())}
                          className="w-8 h-8 bg-gray-300 hover:bg-gray-400 rounded-full font-bold text-gray-700"
                        >
                          -
                        </button>
                        <span className="w-12 text-center font-semibold text-lg">{room.quantity}</span>
                        <button
                          type="button"
                          onClick={() => handleRoomQuantityChange(index, (room.quantity + 1).toString())}
                          className="w-8 h-8 bg-green-600 hover:bg-green-700 rounded-full font-bold text-white"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* Selector de Huéspedes */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label htmlFor="guests" className="block text-sm font-medium text-gray-700 text-center mb-2">Huéspedes Adultos</label>
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleChange({ target: { name: 'guests', value: Math.max(1, parseInt(formData.guests) - 1) }})}
                    className="w-8 h-8 bg-gray-300 hover:bg-gray-400 rounded-full font-bold text-gray-700"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-semibold text-lg">{formData.guests}</span>
                  <button
                    type="button"
                    onClick={() => handleChange({ target: { name: 'guests', value: parseInt(formData.guests) + 1 }})}
                    className="w-8 h-8 bg-green-600 hover:bg-green-700 rounded-full font-bold text-white"
                  >
                    +
                  </button>
                </div>
              </div>
              {/* Selector de Niños */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label htmlFor="children" className="block text-sm font-medium text-gray-700 text-center mb-2">Niños (menores de 12)</label>
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleChange({ target: { name: 'children', value: Math.max(0, parseInt(formData.children) - 1) }})}
                    className="w-8 h-8 bg-gray-300 hover:bg-gray-400 rounded-full font-bold text-gray-700"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-semibold text-lg">{formData.children}</span>
                  <button
                    type="button"
                    onClick={() => handleChange({ target: { name: 'children', value: parseInt(formData.children) + 1 }})}
                    className="w-8 h-8 bg-green-600 hover:bg-green-700 rounded-full font-bold text-white"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            {totalCost > 0 && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-center text-xl font-bold text-green-800">
                  Total: S/. {totalCost.toFixed(2)}
                </p>
                <p className="text-center text-sm text-gray-600 mt-1">
                  {totalNights} {totalNights === 1 ? 'noche' : 'noches'} × {roomTypes.reduce((sum, room) => sum + room.quantity, 0)} {roomTypes.reduce((sum, room) => sum + room.quantity, 0) === 1 ? 'habitación' : 'habitaciones'}
                </p>
              </div>
            )}
          </motion.div>
        );

      case 4:
        return isAdmin ? (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-5"
          >
            <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-4">Paso 4: Información Administrativa</h3>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="mb-4 font-semibold text-gray-800">Seguimiento de Pago</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="amountPaid" className="block text-sm font-medium text-gray-700 mb-2">Monto Pagado (S/.)</label>
                  <input
                    type="number"
                    id="amountPaid"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(parseFloat(e.target.value) || 0)}
                    step="0.01"
                    className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label htmlFor="amountDue" className="block text-sm font-medium text-gray-700 mb-2">Monto Adeudado (S/.)</label>
                  <input
                    type="text"
                    id="amountDue"
                    value={`S/. ${(totalCost - amountPaid).toFixed(2)}`}
                    readOnly
                    className="w-full p-3 text-base border border-gray-300 rounded-lg bg-gray-100 font-bold"
                  />
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="status" className="block mb-2 text-sm font-medium text-gray-700">Estado de la Reserva</label>
              <select id="status" name="status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                <option value="pending">Pendiente</option>
                <option value="confirmed">Confirmada</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Asignar Nº de Habitación</label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
                {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                  <div key={num} className="flex items-center p-2 bg-gray-50 rounded border border-gray-200">
                    <input type="checkbox" id={`room-${num}`} value={num} checked={assignedRoom.includes(num)} onChange={() => handleAssignedRoomChange(num)} className="h-4 w-4 text-green-600 rounded" />
                    <label htmlFor={`room-${num}`} className="ml-2 text-sm text-gray-900 font-medium">{num}</label>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : null;

      default:
        return null;
    }
  };
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black bg-opacity-80 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white overflow-y-auto relative w-screen h-screen rounded-none max-w-none max-h-none sm:rounded-xl sm:max-w-2xl sm:max-h-[90vh] sm:w-full"
          >
            {/* Header */}
            <div className="p-5 sm:p-6 text-center border-b border-gray-200 sticky top-0 bg-green-50 z-10 rounded-t-xl">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors p-2"
              >
                <FaTimes size={20} />
              </button>
              <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-gray-900 mb-2">
                {submissionSuccess
                  ? isAdmin
                    ? 'Cambios Guardados Exitosamente'
                    : '¡Solicitud de Reserva Enviada!'
                  : isAdmin
                    ? existingReservation
                      ? 'Editar Reserva'
                      : 'Crear Nueva Reserva'
                    : 'Reserva tu Estadía'
                }
              </h2>
              <p className="text-gray-500 text-sm">
                {submissionSuccess ? (isAdmin ? 'La nueva reserva ha sido añadida al sistema.' : 'Nos pondremos en contacto contigo pronto.') : 'Completa el formulario para solicitar tu reserva.'}
              </p>
            </div>

            {/* Content */}
            {!submissionSuccess ? (
              <>
                <div className="p-5 sm:p-6">
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {!isAdmin && renderStepIndicator()}
                    <form onSubmit={handleSubmit} id="reservation-form">
                      <AnimatePresence mode="wait">
                        {isAdmin ? (
                          // Admin view is self-contained and has its own submit button
                          renderStep() 
                        ) : (
                          // User view steps
                          renderStep()
                        )}
                      </AnimatePresence>
                    </form>
                  </motion.div>
                </div>
                {/* Themed Footer for Actions */}
                <div className="p-5 sm:p-6 bg-green-50 border-t border-gray-200 sm:rounded-b-xl">
                  {isAdmin ? (
                     <button type="submit" form="reservation-form" className="w-full bg-green-600 text-white p-4 text-lg font-semibold rounded-lg hover:bg-green-700 transition-all" disabled={isSubmitting}>
                        {isSubmitting ? 'Guardando...' : existingReservation ? 'Guardar Cambios' : 'Crear Reserva'}
                      </button>
                  ) : (
                    <>
                      <div className="flex gap-3">
                        {currentStep > 1 && (
                          <button
                            type="button"
                            onClick={prevStep}
                            className="flex-1 bg-gray-300 text-gray-700 p-3 text-base font-semibold rounded-lg hover:bg-gray-400 transition-all flex items-center justify-center gap-2"
                          >
                            <FaArrowLeft /> Anterior
                          </button>
                        )}
                        {currentStep < 3 ? (
                          <button
                            type="button"
                            onClick={nextStep}
                            disabled={!canProceedToNextStep()}
                            className="flex-1 bg-green-600 text-white p-3 text-base font-semibold rounded-lg hover:bg-green-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            Siguiente <FaArrowRight />
                          </button>
                        ) : (
                          <button
                            type="submit"
                            form="reservation-form"
                            disabled={isSubmitting || !canProceedToNextStep()}
                            className="flex-1 bg-green-600 text-white p-3 text-base font-semibold rounded-lg hover:bg-green-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                          </button>
                        )}
                      </div>
                      <div className="text-center mt-6 text-gray-600">
                        <p className="text-sm">O si prefieres, contáctanos directamente:</p>
                        <button onClick={handleWhatsApp} className="mt-2 bg-[#25D366] text-white py-3 px-6 text-sm font-bold rounded-lg inline-flex items-center gap-2 hover:bg-[#20BD5C] transition-colors">
                          <FaWhatsapp size={20} />
                          Contactar por WhatsApp
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="p-5 sm:p-6">
                 <motion.div
                    key="summary"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onAnimationComplete={() => {
                      if (!isAdmin) {
                        downloadSummary(summaryReservation);
                      }
                    }}
                  >
                    {!isAdmin ? (
                      <>
                        <ReservationSummary ref={reservationSummaryRef} reservation={summaryReservation} />
                        <button onClick={onClose} className="mt-8 w-full bg-blue-600 text-white p-3 text-lg font-semibold rounded-lg hover:bg-blue-700">
                          Cerrar
                        </button>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <div className="mb-6">
                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {existingReservation ? '¡Reserva Actualizada!' : '¡Reserva Creada!'}
                          </h3>
                          <p className="text-gray-600">
                            {existingReservation 
                              ? 'Los cambios se han guardado correctamente.'
                              : 'La nueva reserva ha sido añadida al sistema.'}
                          </p>
                        </div>
                        <button onClick={onClose} className="w-full bg-blue-600 text-white p-3 text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                          Cerrar
                        </button>
                      </div>
                    )}
                  </motion.div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReservationModal;
